import type { RootState } from '../..';
import type { ContainerLayoutViewModel, SagaContext, WorkspaceViewModel } from '../../types';
import { waitForAuth } from '../auth/helpers';
import { comparePermissions } from '@thinkalpha/common/util/permissions.js';
import { t } from 'i18next';
import { all, call, getContext, put, race, select, take, takeLatest } from 'redux-saga/effects';
import { firstValueFrom } from 'rxjs';
import { isRoutePath } from 'src/lib/navigation/path';
import { deleteWorkspace } from 'src/lib/workspaces';
// TODO refactor to use a query/mutation from src/queries that can depend on this API call
// eslint-disable-next-line no-restricted-imports -- legacy direct import
import { setDefaultWorkspaceId as changeDefaultWorkspaceOnAPI } from 'src/queries/http/userPreference';
import { convertLegacyToMosaic } from 'src/routes/dashboard/svelte-layout/mosaic-legacy-conversion';
import type { NavigationResult } from 'src/services/NavigationService';
import type { SaveWorkspaceParams } from 'src/services/WorkspaceService';
import { container as staticContainer } from 'src/StaticContainer';
import type { SetSuperuserAccessEnabledUntilAction } from 'src/store/actions/admin';
import { updateMultipleWorkspaceLayouts } from 'src/store/actions/layout';
import { closeActiveModal, openModal } from 'src/store/actions/overlay';
import type { CloseActiveModalAction, CloseWorkspaceSaveModalAction } from 'src/store/actions/overlay';
import { setSnackbar } from 'src/store/actions/ui';
import {
    clearPendingTemplateConfig,
    clearWorkspaceState,
    makeWorkspacePrimary,
    savingCancelled,
    setLayoutModeLocal,
    setPendingTemplateConfig,
} from 'src/store/actions/workspace';
import type {
    CreateFromTemplateLibraryAction,
    SaveCurrentWorkspaceAction,
    SubmitNameForSavingAction,
} from 'src/store/actions/workspace';
import { createRemoteSliceAction } from 'src/store/lib/createRemoteSlice';
import { confirmLeavingWorkspace } from 'src/store/overlays/confirmLeavingWorkspace';
import { getContainerLayoutViewModels } from 'src/store/selectors/layout';
import { getAssertedCurrentWorkspace, getCurrentWorkspace } from 'src/store/selectors/workspace';

const log = staticContainer.get('Logger').getSubLogger({ name: 'workspace-sagas' });

export type SaveWorkspaceFlowResult = 'saved' | 'failed' | 'cancelled';

/**
 * Enter a workspace through the mechanic owned by the current host.
 *
 * Web commits a route, whose entry loads the workspace. Native has no route;
 * its main-owned WorkspaceService performs the state transition directly.
 */
function* enterWorkspace(to: string, workspaceId: string) {
    const container: NonNullable<SagaContext['container']> = yield getContext('container');
    const navigationService = container.getOptional('NavigationService');
    if (!navigationService) {
        const workspaceService = container.get('WorkspaceService');
        yield call([workspaceService, workspaceService.load], workspaceId);
        return { status: 'completed' } satisfies NavigationResult;
    }

    const result: NavigationResult = yield call([navigationService, navigationService.navigate], to);
    return result;
}

export function* onCreateFromTemplateLibrary(action: CreateFromTemplateLibraryAction) {
    yield put(setPendingTemplateConfig(action));
    const result = yield* enterWorkspace('/dashboard/new', 'new');
    if (result.status !== 'completed') {
        yield put(clearPendingTemplateConfig());
    }
}

/**
 * Thin saga shim. Waits for auth, extracts the workspace id from the URL,
 * and delegates load to {@link WorkspaceService.load}. Route changes preserve
 * dirty state, while masquerade identity changes explicitly replace it after
 * re-evaluating the effective user's server-side permissions.
 */
type WorkspaceSyncActionType = 'masq::clear' | 'masq::user-details::set' | 'workspace-service::sync-current-workspace';

export const workspaceSyncActionTypes: WorkspaceSyncActionType[] = [
    'workspace-service::sync-current-workspace',
    'masq::user-details::set',
    'masq::clear',
];

type WorkspaceSyncAction = { readonly type: WorkspaceSyncActionType };

export function* syncCurrentWorkspace(action?: WorkspaceSyncAction) {
    yield* waitForAuth();

    const container: NonNullable<SagaContext['container']> = yield getContext('container');
    const navigationService = container.getOptional('NavigationService');
    const pathname = navigationService?.location.value.pathname;

    if (!pathname || !isRoutePath(pathname, '/dashboard')) {
        return;
    }

    log.debug({ message: 'Checking for workspace sync' });

    const workspaceIdFromUrl = pathname.split('/')[2];
    const workspaceId = workspaceIdFromUrl || 'default';

    const workspaceService = container.get('WorkspaceService');
    if (action?.type === 'masq::clear' || action?.type === 'masq::user-details::set') {
        yield call([workspaceService, workspaceService.load], workspaceId, {
            reason: 'permission-context-change',
        });
        return;
    }
    yield call([workspaceService, workspaceService.load], workspaceId);
}

export function* onRemoveWorkspace() {
    const currentWorkspace: WorkspaceViewModel = yield select(getAssertedCurrentWorkspace);
    const currentlyVisibleWorkspaceId = currentWorkspace.id;

    // The remove confirmation already made this workspace disposable. Clearing
    // it first unregisters the dirty guard before changing destinations.
    yield put(clearWorkspaceState());
    yield* enterWorkspace('/dashboard', 'default');

    // Delete workspace in background
    try {
        yield call(firstValueFrom, deleteWorkspace(currentlyVisibleWorkspaceId));
        const container: NonNullable<SagaContext['container']> = yield getContext('container');
        const queryClient = container.get('QueryClient');
        queryClient.invalidateQueries({ queryKey: ['workspace'] });

        try {
            const analyticsService = container.getOptional('AnalyticsService');
            analyticsService?.track({
                type: 'workspace_deleted',
                workspaceId: currentlyVisibleWorkspaceId,
            });
        } catch (analyticsError) {
            log.warn('Failed to track workspace_deleted', { error: analyticsError });
        }

        yield put(
            setSnackbar(t('views::dashboard::workspace::remove-success'), {
                variant: 'success',
            }),
        );
    } catch (e) {
        yield put(
            setSnackbar(t('views::dashboard::workspace::remove-error'), {
                variant: 'error',
            }),
        );

        if (e instanceof Error) {
            log.error({ message: 'Deleting workspace failed', error: e });
        } else {
            log.fatal({ message: 'unknown exception', details: e });
        }
    }
}

function shouldConfirmTemplateSave(action: SaveCurrentWorkspaceAction, currentWorkspace: WorkspaceViewModel): boolean {
    return (
        action.saveType !== 'saveAs' &&
        currentWorkspace.isTemplate &&
        comparePermissions(currentWorkspace.permissions?.effectiveAccess, 'writer') >= 0
    );
}

function* confirmTemplateSaveIfNeeded(
    action: SaveCurrentWorkspaceAction,
    currentWorkspace: WorkspaceViewModel,
    confirmTemplateSave: boolean,
) {
    if (!confirmTemplateSave || !shouldConfirmTemplateSave(action, currentWorkspace)) {
        return 'confirmed';
    }

    const [modalClosed]: [CloseActiveModalAction<'cancel' | 'saved'>] = yield all([
        take('close-modal'),
        put(openModal({ type: 'verify-saving-workspace' })),
    ]);

    return modalClosed.result === 'cancel' ? 'cancelled' : 'confirmed';
}

export function* runSaveWorkspaceFlow(
    action: SaveCurrentWorkspaceAction,
    { confirmTemplateSave = true }: { confirmTemplateSave?: boolean } = {},
) {
    const currentWorkspace: WorkspaceViewModel = yield select(getAssertedCurrentWorkspace);
    const templateConfirmation = yield* confirmTemplateSaveIfNeeded(action, currentWorkspace, confirmTemplateSave);
    if (templateConfirmation === 'cancelled') {
        return 'cancelled' satisfies SaveWorkspaceFlowResult;
    }

    const isFirstSave = currentWorkspace.isUnsaved;

    let name: string | undefined;
    let promoteToDefault: boolean | undefined;

    if (action.saveType === 'saveAs' || isFirstSave) {
        const modalTitle = isFirstSave
            ? t('views::dashboard::workspace::name-your-workspace', 'Name your workspace')
            : t('views::dashboard::workspace::save-copy-as-new', 'Save copy as new workspace');

        const [modalResult]: [
            { savingSubmitted: SubmitNameForSavingAction } | { modalClosed: CloseWorkspaceSaveModalAction },
        ] = yield all([
            race({
                savingSubmitted: take('submitNameForSaving'),
                modalClosed: take(['close::workspace-save::modal', 'cancel-close::workspace-save::modal']),
            }),
            put(openModal({ type: 'get-new-workspace-name', title: modalTitle })),
        ]);

        if ('modalClosed' in modalResult) {
            yield all([
                put(
                    setSnackbar(t('views::dashboard::workspace::save-canceled', 'Canceled Workspace Saving'), {
                        variant: 'error',
                    }),
                ),
                put(closeActiveModal('name-retrieved')),
                put(savingCancelled()),
            ]);
            return 'cancelled' satisfies SaveWorkspaceFlowResult;
        }

        yield put(closeActiveModal('name-retrieved'));

        name = modalResult.savingSubmitted.name;
        promoteToDefault = modalResult.savingSubmitted.promoteToDefault;
    }

    const container: NonNullable<SagaContext['container']> = yield getContext('container');
    const workspaceService = container.get('WorkspaceService');

    // Construct params narrowed to the correct SaveWorkspaceParams variant.
    // For 'saveAs', `name` is required and we've guaranteed it via the modal
    // exit above; the runtime check satisfies the compiler without an as-cast.
    let saveParams: SaveWorkspaceParams;
    if (action.saveType === 'saveAs') {
        if (name === undefined) {
            // Unreachable: saveAs always opens the modal and returns early on cancel.
            log.error('saveAs reached without a name — should have early-returned');
            return 'failed' satisfies SaveWorkspaceFlowResult;
        }
        saveParams = { saveType: 'saveAs', name, promoteToDefault };
    } else {
        saveParams = { saveType: 'save', name, promoteToDefault };
    }

    try {
        yield call([workspaceService, workspaceService.save], saveParams);
        return 'saved' satisfies SaveWorkspaceFlowResult;
    } catch {
        // WorkspaceService.save already surfaced the failure before rethrowing
        // so programmatic callers can observe it.
        return 'failed' satisfies SaveWorkspaceFlowResult;
    }
}

/**
 * Thin saga shim. Handles the naming modal dance (only saga effects can race
 * `take` against multiple action types cleanly), then delegates persistence to
 * {@link WorkspaceService.save}.
 */
export function* onSaveWorkspace(action: SaveCurrentWorkspaceAction) {
    yield* runSaveWorkspaceFlow(action);
}

export function* onCreateBlankWorkspace() {
    // Web enters /dashboard/new; native directly enters the same unsaved
    // workspace state. The name is requested when the user first saves.
    yield* enterWorkspace('/dashboard/new', 'new');
}

export function* saveThenCloseWorkspace() {
    const workspacePrimary: boolean = yield select(
        (state: RootState) => !!(state.remoteSlice.confirmLeavingWorkspace as any).workspacePrimary, // unsure why we need any cast suddenly
    );

    if (workspacePrimary) {
        const currentWorkspace: WorkspaceViewModel = yield select(getAssertedCurrentWorkspace);
        yield put(makeWorkspacePrimary(currentWorkspace.id));
    }

    const saveResult = yield* runSaveWorkspaceFlow({ saveType: 'save', type: 'saveWorkspaceEvent' });
    if (saveResult !== 'saved') {
        return;
    }

    yield put(createRemoteSliceAction(confirmLeavingWorkspace, 'closeModal', 'saved'));
}

export function* onSaveWorkspaceAsPrimary(action: ReturnType<typeof makeWorkspacePrimary>) {
    yield changeDefaultWorkspaceOnAPI(action.id);

    try {
        const container: NonNullable<SagaContext['container']> = yield getContext('container');
        const analyticsService = container.getOptional('AnalyticsService');
        analyticsService?.track({
            type: 'workspace_set_default',
            workspaceId: action.id,
        });
    } catch (analyticsError) {
        log.warn('Failed to track workspace_set_default', { error: analyticsError });
    }
}

const conversionLog = log.getSubLogger({ name: 'convertWorkspaceLayoutsToMosaic' });

/**
 * Convert all container layouts in the current workspace to mosaic-compatible layouts.
 * This normalizes positions/sizes to the grid, resolves overlaps, and enforces minimum sizes.
 */
function* convertWorkspaceLayoutsToMosaic() {
    const currentWorkspace: WorkspaceViewModel = yield select(getAssertedCurrentWorkspace);
    const containerLayouts: Record<string, ContainerLayoutViewModel> = yield select(getContainerLayoutViewModels);

    const containerCount = Object.keys(containerLayouts).length;
    if (containerCount === 0) {
        conversionLog.info({
            message: 'No containers to convert',
            workspaceId: currentWorkspace.id,
            workspaceName: currentWorkspace.name,
        });
        return;
    }

    conversionLog.info({
        message: 'Starting layout conversion',
        workspaceId: currentWorkspace.id,
        workspaceName: currentWorkspace.name,
        containerCount,
    });

    // Convert ContainerLayoutViewModel to NormalizedLayout for the conversion function
    const legacyLayouts: Record<string, { x: number; y: number; w: number; h: number }> = {};
    for (const [containerId, layout] of Object.entries(containerLayouts)) {
        legacyLayouts[containerId] = {
            x: layout.x,
            y: layout.y,
            w: layout.w,
            h: layout.h,
        };
    }

    // Run the conversion
    const convertedLayouts = convertLegacyToMosaic(legacyLayouts);

    // Track which layouts changed
    const changedLayouts: string[] = [];
    for (const [containerId, newLayout] of Object.entries(convertedLayouts)) {
        const oldLayout = legacyLayouts[containerId];
        if (
            oldLayout.x !== newLayout.x ||
            oldLayout.y !== newLayout.y ||
            oldLayout.w !== newLayout.w ||
            oldLayout.h !== newLayout.h
        ) {
            changedLayouts.push(containerId);
        }
    }

    // Create layout updates for each converted layout
    const updates = Object.entries(convertedLayouts).map(([containerId, layout]) => ({
        containerId,
        layout: {
            x: layout.x,
            y: layout.y,
            w: layout.w,
            h: layout.h,
        },
    }));

    if (updates.length > 0) {
        yield put(updateMultipleWorkspaceLayouts(updates));

        conversionLog.info({
            message: 'Layout conversion completed',
            workspaceId: currentWorkspace.id,
            workspaceName: currentWorkspace.name,
            totalContainers: containerCount,
            changedContainers: changedLayouts.length,
            changedContainerIds: changedLayouts,
            beforeLayouts: legacyLayouts,
            afterLayouts: convertedLayouts,
        });
    }
}

export function* onToggleLayoutMode() {
    const currentWorkspace: WorkspaceViewModel = yield select(getAssertedCurrentWorkspace);
    const willBeMosaic = !currentWorkspace.layoutSettings?.enableMosaicLayout;

    if (willBeMosaic) {
        yield* convertWorkspaceLayoutsToMosaic();
    }

    yield put(setLayoutModeLocal(willBeMosaic));
}

function* openWorkspaceTemplateLibrary() {
    yield put(openModal({ type: 'workspace-library' }));
}

function* checkIfWorkspaceNeedsToSync(action: SetSuperuserAccessEnabledUntilAction) {
    const container: NonNullable<SagaContext['container']> = yield getContext('container');
    const navigationService = container.getOptional('NavigationService');
    const pathname = navigationService?.location.value.pathname;
    if (!pathname || !isRoutePath(pathname, '/dashboard')) {
        return;
    }

    if (action.superuserAccessEnabledUntil) {
        const currentWorkspace: WorkspaceViewModel | null = yield select(getCurrentWorkspace);
        if (!currentWorkspace) {
            // Did not have access before
            yield call(syncCurrentWorkspace);
        }
    }
}

export function* workspaceSagas() {
    yield all([
        takeLatest('removeWorkspaceEvent', onRemoveWorkspace),
        takeLatest('saveWorkspaceEvent', onSaveWorkspace),
        takeLatest('createBlankWorkspaceEvent', onCreateBlankWorkspace),
        takeLatest('createFromTemplateLibraryEvent', onCreateFromTemplateLibrary),
        takeLatest(confirmLeavingWorkspace.actionTypes.saveThenCloseModal, saveThenCloseWorkspace),
        takeLatest('saveWorkspaceAsPrimary', onSaveWorkspaceAsPrimary),
        takeLatest('workspace-template-library::open', openWorkspaceTemplateLibrary),
        takeLatest('toggleLayoutMode', onToggleLayoutMode),
        takeLatest('superuser::set-expiration', checkIfWorkspaceNeedsToSync),
        takeLatest(workspaceSyncActionTypes, syncCurrentWorkspace),
    ]);
}
